// src/pages/admin/integrations/shopify/components/SettingsTab.tsx
import { useState, type FC, type FormEvent } from 'react';
import { ShopifyInfo, ShopifySettings, ToastFn } from '../../../types/integrations';
import { SETTING_FIELDS } from '../../../util/Constants';
import ToggleSwitch from '../../../components/common/ToggleSwitch';

const inputCls =
  'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

interface Props {
  info: ShopifyInfo;
  onSaved: () => void;
  toast: ToastFn;
}

const SettingsTab: FC<Props> = ({ info, onSaved, toast }) => {
  const [s, setS] = useState<ShopifySettings>(info.settings ?? {});
  const [busy, setBusy] = useState(false);

  if (info.status === 'NOT_CONNECTED') {
    return (
      <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
        Connect first to change settings.
      </div>
    );
  }

  const save = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setBusy(true);
    try {
      // await api.saveSettings('SHOPIFY', s);
      toast('Settings saved');
      onSaved();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Save failed', 'bad');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="bg-white rounded-xl border border-slate-200 p-5 max-w-lg flex flex-col gap-4"
      onSubmit={save}
    >
      {SETTING_FIELDS.map(([k, label, hint, isFlag]) =>
        isFlag ? (
          <label key={k} className="flex items-center gap-2">
            <ToggleSwitch
              checked={Boolean(s[k as keyof ShopifySettings])}
              label={label}
              onChange={(v) => setS({ ...s, [k]: v })}
            />
            <span className="text-sm text-slate-700">{label}</span>
          </label>
        ) : (
          <label key={k} className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-slate-700">{label}</span>
            <input
              value={(s[k as keyof ShopifySettings] as string) ?? ''}
              onChange={(e) => setS({ ...s, [k]: e.target.value })}
              className={inputCls}
            />
            {hint && <span className="text-xs text-slate-500">{hint}</span>}
          </label>
        )
      )}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={busy}
          className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
        >
          {busy ? 'Saving…' : 'Save settings'}
        </button>
      </div>
    </form>
  );
};

export default SettingsTab;